import { Client, Conversation } from '@twilio/conversations'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

import { useMarketplacesQuery } from 'src/queries/shared/marketplaces/marketplaces'

import useCurrentMarketplaceId from '../../buyer-dashboard/shared/hooks/marketplaces/useCurrentMarketplaceId'
import useChatContacts from '../chat-parts/hooks/useChatContacts/useChatContacts'
import { calculateConversationUnreadCount, formatConversations } from '../store/chatStore.utils'

interface ChatStateActions {
  addConversation: (conversation: Conversation) => void
  setConversationClient: (conversationClient: Client | null) => void
  setConversations: (conversations?: Conversation[]) => void
  setIsConversationsLoading: (isLoading: boolean) => void
  removeConversation: (conversationSid: string) => void
  updateConversation: (conversation: Conversation) => void
}

export interface ChatState {
  conversationClient: Client | null
  conversations: Conversation[]
  isConversationsLoading: boolean
  actions: ChatStateActions
}

export const useChatStore = createWithEqualityFn<ChatState>()(
  set => ({
    conversationClient: null,
    conversations: [],
    isConversationsLoading: false,
    actions: {
      addConversation: conversation =>
        set(state => ({ conversations: [...state.conversations, conversation] })),
      setConversationClient: conversationClient => set(() => ({ conversationClient })),
      setConversations: conversations => set(() => ({ conversations })),
      setIsConversationsLoading: isLoading => set(() => ({ isConversationsLoading: isLoading })),
      removeConversation: conversationSid =>
        set(state => ({
          conversations: {
            ...state.conversations.filter(conversation => conversationSid !== conversation.sid),
          },
        })),
      updateConversation: conversationUpdated =>
        set(state => ({
          conversations: state.conversations.map(conversation =>
            conversation.sid === conversationUpdated.sid ? conversationUpdated : conversation
          ),
        })),
    },
  }),
  shallow
)

export const useChatActions = () => useChatStore(state => state.actions)

export const useChatConversations = () =>
  useChatStore(state => {
    const { contacts } = useChatContacts()
    const { data: ecosystems = [] } = useMarketplacesQuery()
    const currentMarketplaceId = useCurrentMarketplaceId()

    return formatConversations({
      contacts,
      conversations: state.conversations,
      currentMarketplaceId,
      ecosystems,
    })
  })

export const useChatConversationClient = () => useChatStore(state => state.conversationClient)

export const useIsChatConversationsLoading = () =>
  useChatStore(state => state.isConversationsLoading)

export const useUnreadSellerChatMessagesCount = () =>
  useChatStore(state => {
    return state.conversations.reduce(
      (total, conversation) => total + calculateConversationUnreadCount(conversation),
      0
    )
  })
